import { Card, Skeleton } from '@vnetwork/ui/primitives';
import { Chart } from '@vnetwork/ui/charts';
import styles from './ManticoreTrafficCards.module.css';

export interface TrafficMetric {
  label: string;
  value: string;
  /** Sparkline samples — `t` is a time label (tooltip x), `v` the value. */
  data: { t: string; v: number }[];
  /** Short, unit-aware formatter for the hover tooltip value. */
  format?: (v: number) => string;
  /** Optional muted caption under the value (e.g. the day-tier peak caveat). */
  hint?: string;
}

/** Sparkline accent — matches the @ui dashboard recipe (MetricSparkCard). */
const SPARK_COLOR = 'var(--vn-color-blue-400)';

/**
 * Headline traffic block for the Manticore overview — a uniform stack of
 * metric cards, each showing label + value on the left and a 72px area
 * sparkline on the right. Mirrors the @ui Dashboard recipe's MetricSparkCard
 * so the overview reads as a calm, even KPI list rather than a mixed grid.
 */
export function ManticoreTrafficCards({ items, loading }: { items: TrafficMetric[]; loading?: boolean }) {
  if (loading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} style={{ height: 96 }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {items.map((m) => (
        <Card key={m.label} flat>
          <div className={styles.metricRow}>
            <div>
              <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{m.label}</div>
              <div style={{ fontSize: 'var(--vn-font-size-3xl)', fontWeight: 'var(--vn-font-weight-bold)', lineHeight: 1.15 }}>{m.value}</div>
              {m.hint ? (
                <div style={{ marginTop: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', lineHeight: 1.3 }}>
                  {m.hint}
                </div>
              ) : null}
            </div>
            <Chart
              type="area"
              data={m.data}
              xKey="t"
              series={[{ key: 'v', label: m.label, color: SPARK_COLOR }]}
              height={72}
              showGrid={false}
              showXAxis={false}
              showYAxis={false}
              valueFormatter={m.format ? (v) => m.format!(Number(v)) : undefined}
            />
          </div>
        </Card>
      ))}
    </div>
  );
}
